import React, { useState, useEffect } from 'react';

const PricingWidget = () => {
    const [packages, setPackages] = useState({});
    const [selectedPeriod, setSelectedPeriod] = useState('annually'); // annually, semiannually, monthly
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchPackages();
    }, []);

    const fetchPackages = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
            const response = await fetch(`${backendUrl}/api/subscriptions/packages`);
            if (!response.ok) throw new Error('Failed to fetch packages');
            const data = await response.json();
            setPackages(data.packages);
        } catch (err) {
            console.error('Error fetching packages:', err);
            setError('Kunne ikke laste prisinformasjon');
        }
    };

    // Fixed pricing structure matching aktivlogg.no exactly
    const pricingMatrix = {
        start: {
            annually: { price: 299, savings: "Spar 20%" },
            semiannually: { price: 329, savings: null },
            monthly: { price: 359, savings: null }
        },
        unlimited: {
            annually: { price: 499, savings: "Spar 20%" },
            semiannually: { price: 529, savings: null },
            monthly: { price: 618, savings: null }
        }
    };

    const getPeriodPricing = (packageType) => {
        const pricing = pricingMatrix[packageType]?.[selectedPeriod];
        if (!pricing) return { price: 0, savings: null };
        
        return {
            price: pricing.price,
            label: 'pr. mnd',
            savings: pricing.savings,
            totalLabel: {
                annually: '(Faktureres årlig)',
                semiannually: '(Faktureres halvårlig)',
                monthly: '(Faktureres månedlig)'
            }[selectedPeriod]
        };
    };

    const handleCheckout = async (packageType) => {
        setLoading(true);
        setError('');
        
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
            const parentUrl = document.referrer || window.location.origin;
            
            const response = await fetch(`${backendUrl}/api/subscriptions/checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    subscription_type: packageType,
                    host_url: parentUrl,
                    period: selectedPeriod,
                    metadata: {
                        source: 'pricing_widget',
                        period: selectedPeriod,
                        package: packageType,
                        referrer: parentUrl
                    }
                })
            });

            if (!response.ok) throw new Error('Failed to create checkout session');
            
            const data = await response.json();
            
            if (data.url) {
                // Check if we're in an iframe
                if (window.parent !== window) {
                    // In iframe - send message to parent
                    window.parent.postMessage({
                        type: 'STRIPE_CHECKOUT',
                        url: data.url,
                        session_id: data.session_id,
                        package: packageType,
                        period: selectedPeriod
                    }, '*');
                } else {
                    // Standalone - redirect directly
                    window.location.href = data.url;
                }
            }
        } catch (err) {
            console.error('Checkout error:', err);
            setError('Kunne ikke starte betalingsprosessen. Vennligst prøv igjen.');
        } finally {
            setLoading(false);
        }
    };

    if (error) {
        return (
            <div className="min-h-screen bg-[#1E2330] flex items-center justify-center p-4">
                <div className="text-white text-center">
                    <h2 className="text-xl font-bold mb-2">Feil</h2>
                    <p className="mb-4">{error}</p>
                    <button 
                        onClick={fetchPackages}
                        className="px-6 py-2 bg-[#FFD700] text-[#1E2330] font-bold rounded-full hover:bg-yellow-400 transition-colors"
                    >
                        Prøv igjen
                    </button>
                </div>
            </div>
        );
    }

    const startPackage = packages.start;
    const unlimitedPackage = packages.unlimited;

    if (!startPackage || !unlimitedPackage) {
        return (
            <div className="min-h-screen bg-[#1E2330] flex items-center justify-center">
                <div className="text-white text-center">
                    <div className="animate-spin w-8 h-8 border-4 border-[#FFD700] border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p>Loading pricing...</p>
                </div>
            </div>
        );
    }

    const startPricing = getPeriodPricing('start');
    const unlimitedPricing = getPeriodPricing('unlimited');

    return (
        <div className="min-h-screen bg-[#1E2330] p-4 font-sans">
            {/* Period Selection Tabs */}
            <div className="max-w-4xl mx-auto mb-8">
                <div className="flex justify-center gap-2">
                    <button
                        onClick={() => setSelectedPeriod('annually')}
                        className={`px-6 py-3 rounded-lg font-medium transition-all ${
                            selectedPeriod === 'annually'
                                ? 'bg-[#FFD700] text-[#1E2330]'
                                : 'bg-[#2A3142] text-white border-2 border-[#FFD700] hover:bg-[#FFD700] hover:text-[#1E2330]'
                        }`}
                    >
                        Årlig
                    </button>
                    <button
                        onClick={() => setSelectedPeriod('semiannually')}
                        className={`px-6 py-3 rounded-lg font-medium transition-all ${
                            selectedPeriod === 'semiannually'
                                ? 'bg-[#FFD700] text-[#1E2330]'
                                : 'bg-[#2A3142] text-white border-2 border-[#FFD700] hover:bg-[#FFD700] hover:text-[#1E2330]'
                        }`}
                    >
                        Halvårlig
                    </button>
                    <button
                        onClick={() => setSelectedPeriod('monthly')}
                        className={`px-6 py-3 rounded-lg font-medium transition-all ${
                            selectedPeriod === 'monthly'
                                ? 'bg-[#FFD700] text-[#1E2330]'
                                : 'bg-[#2A3142] text-white border-2 border-[#FFD700] hover:bg-[#FFD700] hover:text-[#1E2330]'
                        }`}
                    >
                        Pr. mnd
                    </button>
                </div>
            </div>

            {/* Pricing Cards */}
            <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                    {/* START Package */}
                    <div className="relative bg-[#2A3142] rounded-lg p-8 text-white shadow-lg">
                        {startPricing.savings && (
                            <div className="absolute top-0 right-0 bg-[#66D468] text-white px-4 py-2 text-sm font-bold">
                                {startPricing.savings}
                            </div>
                        )}
                        
                        <div className="text-center mb-8">
                            <h3 className="text-3xl font-bold text-white mb-4">{startPackage.name}</h3>
                            <div className="text-[#FFD700] text-6xl font-bold mb-1">
                                {startPricing.price}
                            </div>
                            <div className="text-[#FFD700] text-lg mb-2">{startPricing.label}</div>
                            <div className="text-sm text-[#E0E0E0]">{startPricing.totalLabel}</div>
                        </div>

                        <div className="space-y-4 mb-8">
                            {startPackage.features.map((feature, index) => (
                                <div key={index} className="flex items-start text-[#E0E0E0]">
                                    <div className="w-6 h-6 bg-[#66D468] rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-0.5">
                                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <span className="text-lg leading-relaxed">{feature}</span>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={() => handleCheckout('start')}
                            disabled={loading}
                            className="w-full bg-[#FFD700] text-[#1E2330] py-4 px-8 rounded-lg text-lg font-bold hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'PROSESSERER...' : 'BESTILL'}
                        </button>
                    </div>

                    {/* UNLIMITED Package */}
                    <div className="relative bg-[#2A3142] rounded-lg p-8 text-white shadow-lg border-2 border-[#FFD700]">
                        {unlimitedPricing.savings && (
                            <div className="absolute top-0 right-0 bg-[#66D468] text-white px-4 py-2 text-sm font-bold">
                                {unlimitedPricing.savings}
                            </div>
                        )}
                        
                        <div className="text-center mb-8">
                            <h3 className="text-3xl font-bold text-white mb-4">{unlimitedPackage.name}</h3>
                            <div className="text-[#FFD700] text-6xl font-bold mb-1">
                                {unlimitedPricing.price}
                            </div>
                            <div className="text-[#FFD700] text-lg mb-2">{unlimitedPricing.label}</div>
                            <div className="text-sm text-[#E0E0E0]">{unlimitedPricing.totalLabel}</div>
                        </div>

                        <div className="space-y-4 mb-8">
                            {unlimitedPackage.features.map((feature, index) => (
                                <div key={index} className="flex items-start text-[#E0E0E0]">
                                    <div className="w-6 h-6 bg-[#66D468] rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-0.5">
                                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <span className="text-lg leading-relaxed">{feature}</span>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={() => handleCheckout('unlimited')}
                            disabled={loading}
                            className="w-full bg-[#FFD700] text-[#1E2330] py-4 px-8 rounded-lg text-lg font-bold hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'PROSESSERER...' : 'BESTILL'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="max-w-4xl mx-auto mt-6">
                    <div className="bg-red-600 text-white p-4 rounded-lg text-center">
                        {error}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PricingWidget;